import { db } from '../lib/firebase';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { storage } from '../lib/firebase';

export interface UserDataExport {
  profile: any;
  content: any[];
  comments: any[];
  likes: any[];
  subscriptions: any[];
  watchlist: any[];
  analytics: any[];
  donations: any[];
}

/**
 * Export all user data for GDPR compliance
 */
export async function exportUserData(userId: string): Promise<UserDataExport> {
  const exportData: UserDataExport = {
    profile: null,
    content: [],
    comments: [],
    likes: [],
    subscriptions: [],
    watchlist: [],
    analytics: [],
    donations: [],
  };

  try {
    // Export user profile
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      exportData.profile = { id: userDoc.id, ...userDoc.data() };
    }

    // Export user's content
    const contentQuery = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId)
    );
    const contentSnapshot = await getDocs(contentQuery);
    exportData.content = contentSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Export comments
    const commentsQuery = query(
      collection(db, 'comments'),
      where('userId', '==', userId)
    );
    const commentsSnapshot = await getDocs(commentsQuery);
    exportData.comments = commentsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Export likes
    const likesQuery = query(
      collection(db, 'likes'),
      where('userId', '==', userId)
    );
    const likesSnapshot = await getDocs(likesQuery);
    exportData.likes = likesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Export subscriptions
    const subscriptionsQuery = query(
      collection(db, 'subscriptions'),
      where('subscriberId', '==', userId)
    );
    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
    exportData.subscriptions = subscriptionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Export watchlist
    const watchlistQuery = query(
      collection(db, 'watchlists'),
      where('userId', '==', userId)
    );
    const watchlistSnapshot = await getDocs(watchlistQuery);
    exportData.watchlist = watchlistSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Export analytics
    const analyticsQuery = query(
      collection(db, 'analytics_views'),
      where('userId', '==', userId)
    );
    const analyticsSnapshot = await getDocs(analyticsQuery);
    exportData.analytics = analyticsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Export donations
    const donationsQuery = query(
      collection(db, 'donations'),
      where('donorId', '==', userId)
    );
    const donationsSnapshot = await getDocs(donationsQuery);
    exportData.donations = donationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    return exportData;
  } catch (error) {
    console.error('Error exporting user data:', error);
    throw new Error('Failed to export user data');
  }
}

/**
 * Delete all user data for GDPR right to be forgotten
 */
export async function deleteUserData(userId: string): Promise<void> {
  try {
    // Delete user profile
    await deleteDoc(doc(db, 'users', userId));

    // Delete user's content (soft delete - mark as removed)
    const contentQuery = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId)
    );
    const contentSnapshot = await getDocs(contentQuery);
    for (const contentDoc of contentSnapshot.docs) {
      // Mark as removed instead of deleting to preserve references
      await updateDoc(doc(db, 'content', contentDoc.id), {
        status: 'removed',
        deletedAt: new Date(),
      });
    }

    // Delete comments
    const commentsQuery = query(
      collection(db, 'comments'),
      where('userId', '==', userId)
    );
    const commentsSnapshot = await getDocs(commentsQuery);
    for (const commentDoc of commentsSnapshot.docs) {
      await deleteDoc(doc(db, 'comments', commentDoc.id));
    }

    // Delete likes
    const likesQuery = query(
      collection(db, 'likes'),
      where('userId', '==', userId)
    );
    const likesSnapshot = await getDocs(likesQuery);
    for (const likeDoc of likesSnapshot.docs) {
      await deleteDoc(doc(db, 'likes', likeDoc.id));
    }

    // Delete subscriptions
    const subscriptionsQuery = query(
      collection(db, 'subscriptions'),
      where('subscriberId', '==', userId)
    );
    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
    for (const subDoc of subscriptionsSnapshot.docs) {
      await deleteDoc(doc(db, 'subscriptions', subDoc.id));
    }

    // Delete watchlist
    const watchlistQuery = query(
      collection(db, 'watchlists'),
      where('userId', '==', userId)
    );
    const watchlistSnapshot = await getDocs(watchlistQuery);
    for (const watchlistDoc of watchlistSnapshot.docs) {
      await deleteDoc(doc(db, 'watchlists', watchlistDoc.id));
    }

    // Delete user storage files
    try {
      const userStorageRef = ref(storage, `users/${userId}`);
      const fileList = await listAll(userStorageRef);
      
      for (const itemRef of fileList.items) {
        await deleteObject(itemRef);
      }
    } catch (storageError) {
      console.error('Error deleting user storage files:', storageError);
      // Continue even if storage deletion fails
    }
  } catch (error) {
    console.error('Error deleting user data:', error);
    throw new Error('Failed to delete user data');
  }
}

